import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  VideoIcon,
  Star,
  Clock,
  Shield,
  CheckCircle2,
  MessageCircle,
  Calendar,
  Award,
  BadgeCheck,
  Users,
  Sparkles,
  Heart,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  Stethoscope,
  GraduationCap
} from 'lucide-react'
import { Button } from "@/components/ui/button"

const VetsSection = () => {
  const [activeVet, setActiveVet] = useState(0);
  const [showAllReviews, setShowAllReviews] = useState(false);
  
  const vets = [
    {
      name: "Dra. Alejandra Morales",
      specialty: "Medicina Preventiva",
      image: "/vets1.png",
      rating: 4.9,
      reviews: 128,
      available: true,
      experience: "8+ años",
      education: "Universidad Nacional Autónoma de México",
      certifications: ["Medicina Preventiva", "Nutrición Canina"],
      bio: "Especialista en medicina preventiva con enfoque en detección temprana de problemas de salud en mascotas utilizando tecnología avanzada.",
      reviewHighlight: {
        text: "La Dra. Morales detectó un problema cardíaco en mi perro que otros veterinarios no habían notado. Increíblemente profesional y atenta.",
        author: "Martín G.",
        rating: 5
      }
    },
    {
      name: "Dra. Carmen Ortiz",
      specialty: "Cirugía Veterinaria",
      image: "/vets2.png",
      rating: 4.8,
      reviews: 156,
      available: true,
      experience: "12+ años",
      education: "Universidad Autónoma de Barcelona",
      certifications: ["Cirugía Avanzada", "Ortopedia"],
      bio: "Cirujana veterinaria con amplia experiencia en procedimientos complejos y recuperación post-operatoria de mascotas.",
      reviewHighlight: {
        text: "Excelente profesional. La Dra. Ortiz realizó una consulta detallada y nos dio recomendaciones muy precisas para el cuidado de nuestra mascota.",
        author: "Laura T.",
        rating: 5
      }
    },
    {
      name: "Dr. Miguel Ángel Mendoza",
      specialty: "Dermatología Veterinaria",
      image: "/vets3.png",
      rating: 4.9,
      reviews: 142,
      available: false,
      experience: "10+ años",
      education: "Universidad Complutense de Madrid",
      certifications: ["Dermatología Clínica", "Alergología"],
      bio: "Especialista en problemas dermatológicos y alergias en mascotas, con enfoque en tratamientos innovadores y mínimamente invasivos.",
      reviewHighlight: {
        text: "El Dr. Mendoza identificó rápidamente la alergia que afectaba a mi perro. Su tratamiento fue efectivo y ahora mi mascota está mucho mejor.",
        author: "Carlos P.",
        rating: 5
      }
    }
  ];

  const features = [
    {
      icon: Clock,
      title: "Disponibilidad 24/7",
      description: "Atención veterinaria cuando la necesites, sin importar la hora o el día",
      color: "from-amber-400 to-orange-400"
    },
    {
      icon: Shield,
      title: "Veterinarios Certificados",
      description: "Profesionales con años de experiencia.",
      color: "from-green-400 to-emerald-400"
    },
    {
      icon: VideoIcon,
      title: "Consultas Remotas",
      description: "Desde la comodidad de tu hogar, sin estrés para tu mascota",
      color: "from-blue-400 to-cyan-400"
    }
  ];
  
  const stats = [
    { value: "98%", label: "Satisfacción" },
    { value: "15min", label: "Tiempo de respuesta" },
    { value: "+200", label: "Veterinarios" }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };
  
  const handlePrevVet = () => {
    setActiveVet((prev) => (prev === 0 ? vets.length - 1 : prev - 1));
  };
  
  const handleNextVet = () => {
    setActiveVet((prev) => (prev === vets.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="relative overflow-hidden py-12 sm:py-16 lg:py-20" id="vets">
      {/* Enhanced Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50 to-white" />
        <div className="absolute inset-0 opacity-5" />
        
        {/* Animated Background Elements */}
        <motion.div 
          className="absolute top-1/3 right-[15%] w-64 h-64 bg-amber-200/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div 
          className="absolute bottom-1/3 left-[15%] w-64 h-64 bg-orange-200/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 relative">
        {/* Enhanced Header */}
        <div className="text-center mb-10 sm:mb-14 lg:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-1.5 sm:gap-2 bg-white/80 backdrop-blur-sm 
                     px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-sm mb-4 sm:mb-6"
          >
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
            <span className="text-amber-900 text-sm sm:text-base font-medium">Atención Veterinaria Premium</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4"
          >
            <span className="block text-amber-900">Veterinarios especialistas</span>
            <span className="bg-gradient-to-r from-amber-600 to-orange-600 text-transparent bg-clip-text">
              disponibles para tu mascota
            </span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-base sm:text-lg lg:text-xl text-amber-700 max-w-3xl mx-auto px-4"
          >
            Consulta con profesionales certificados desde la comodidad de tu hogar,
            con atención personalizada y seguimiento continuo para tu mejor amigo
          </motion.p>
          
          {/* Trust Indicators */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-3 sm:gap-5 mt-6"
          >
            <div className="flex items-center gap-1.5 text-sm sm:text-base text-amber-800">
              <BadgeCheck className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
              <span>Certificados y avalados</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm sm:text-base text-amber-800">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
              <span>+2,000 consultas realizadas</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm sm:text-base text-amber-800">
              <Award className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
              <span>Servicio Online de calidad</span>
            </div>
          </motion.div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="grid grid-cols-3 gap-2 sm:gap-4 max-w-3xl mx-auto mb-10 sm:mb-14"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white/90 backdrop-blur-sm rounded-xl p-3 sm:p-4 
                       border border-amber-100/70 shadow-sm text-center"
            >
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-amber-600 mb-1">
                {stat.value}
              </p>
              <p className="text-xs sm:text-sm text-amber-700">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Enhanced Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-14 lg:mb-16">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/90 backdrop-blur-sm rounded-xl p-5 sm:p-6 shadow-md
                       border border-amber-100/70 hover:shadow-lg transition-all duration-300"
            >
              <div className={`bg-gradient-to-br ${feature.color} w-12 h-12 sm:w-14 sm:h-14 
                             rounded-xl flex items-center justify-center mb-4 sm:mb-5 text-white`}>
                <feature.icon className="h-6 w-6 sm:h-7 sm:w-7" />
              </div>
              <h3 className="font-semibold text-amber-900 text-lg sm:text-xl mb-2 sm:mb-3">{feature.title}</h3>
              <p className="text-amber-700 text-sm sm:text-base">{feature.description}</p>
              
              <div className="mt-4 pt-4 border-t border-amber-100">
                <div className="flex items-center text-amber-600 text-sm font-medium">
                  <span>Saber más</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Enhanced Vets Carousel */}
        <div className="mb-10 sm:mb-14 lg:mb-16 relative">
          <div className="flex justify-between items-center mb-6 sm:mb-8">
            <motion.h3
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-xl sm:text-2xl lg:text-3xl font-bold text-amber-900"
            >
              Nuestros especialistas destacados
            </motion.h3>
            
            <div className="flex gap-2">
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white w-10 h-10 rounded-full flex items-center justify-center 
                         shadow-sm border border-amber-100 hover:bg-amber-50 transition-colors"
                onClick={handlePrevVet}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronLeft className="h-5 w-5 text-amber-600" />
              </motion.button>
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white w-10 h-10 rounded-full flex items-center justify-center 
                         shadow-sm border border-amber-100 hover:bg-amber-50 transition-colors"
                onClick={handleNextVet}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronRight className="h-5 w-5 text-amber-600" />
              </motion.button>
            </div>
          </div>
          
          <div className="relative">
            <AnimatePresence mode="wait">
              {vets.map((vet, index) => (
                activeVet === index && (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg 
                             border border-amber-100/70"
                  >
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
                      {/* Vet Image and Basic Info */}
                      <div className="lg:col-span-2 relative">
                        <div className="relative h-64 sm:h-72 md:h-full">
                <img
                  src={vet.image}
                  alt={vet.name}
                  className="w-full h-full object-cover"
                />
                {/* Availability Badge */}
                          <div className={`absolute top-4 right-4 px-3.5 py-1.5 
                                        rounded-full text-sm font-medium
                              ${vet.available
                    ? 'bg-green-500 text-white'
                    : 'bg-amber-100 text-amber-600'}`}>
                            {vet.available ? 'Disponible ahora' : 'En consulta'}
                          </div>
                          
                          {/* Experience Badge */}
                          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm 
                                        px-3 py-1.5 rounded-full shadow-md border border-amber-100">
                            <div className="flex items-center gap-1.5">
                              <GraduationCap className="h-4 w-4 text-amber-600" />
                              <span className="text-sm font-medium text-amber-900">{vet.experience} de experiencia</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Vet Details */}
                      <div className="lg:col-span-3 p-5 sm:p-6 md:p-8">
                        <div className="flex flex-col h-full">
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-bold text-xl sm:text-2xl text-amber-900">{vet.name}</h3>
                              <div className="flex items-center gap-1">
                                <Star className="h-5 w-5 fill-amber-500 text-amber-500" />
                                <span className="font-semibold text-amber-900">{vet.rating}</span>
                                <span className="text-amber-600 text-sm">({vet.reviews})</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mb-4">
                              <Stethoscope className="h-5 w-5 text-amber-600" />
                              <span className="text-amber-700 font-medium">{vet.specialty}</span>
                            </div>
                            
                            <p className="text-amber-700 mb-4">{vet.bio}</p>
                            
                            {/* Certifications */}
                            <div className="mb-4">
                              <h4 className="font-semibold text-amber-900 mb-2">Certificaciones:</h4>
                              <div className="flex flex-wrap gap-2">
                                {vet.certifications.map((cert, i) => (
                                  <span key={i} className="bg-amber-100/70 text-amber-800 px-3 py-1 rounded-full text-sm">
                                    {cert}
                                  </span>
                                ))}
                </div>
              </div>

                            {/* Review Highlight */}
                            <div className="bg-amber-50/70 rounded-lg p-4 mb-4 border border-amber-100">
                              <div className="flex items-center gap-1 mb-2">
                                {[...Array(5)].map((_, i) => (
                                  <Star key={i} className={`h-4 w-4 ${i < vet.reviewHighlight.rating ? 'fill-amber-500 text-amber-500' : 'text-amber-200'}`} />
                                ))}
                              </div>
                              <p className="text-amber-700 text-sm italic mb-2">"{vet.reviewHighlight.text}"</p>
                              <p className="text-amber-600 text-xs font-medium">- {vet.reviewHighlight.author}</p>
                            </div>
                          </div>
                          
                          {/* CTA Button */}
                          <div className="mt-auto">
                            {vet.available ? (
                  <Button
                                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 
                                         text-white py-3 text-base sm:text-lg rounded-xl shadow-md hover:shadow-lg"
                    onClick={() => window.location.href = '/?sign-in=true'}
                  >
                    Agendar Consulta
                                <VideoIcon className="ml-2 h-5 w-5" />
                              </Button>
                            ) : (
                              <div className="flex flex-col gap-3">
                                <Button
                                  className="w-full bg-white border border-amber-200 text-amber-700 hover:bg-amber-50
                                           py-3 text-base rounded-xl shadow-sm"
                                  onClick={() => window.location.href = '/?sign-in=true'}
                                >
                                  Agendar para más tarde
                                  <Calendar className="ml-2 h-5 w-5" />
                  </Button>
                                <p className="text-center text-amber-600 text-sm">
                                  Disponible en aproximadamente 45 minutos
                                </p>
                              </div>
                )}
                          </div>
                        </div>
                      </div>
              </div>
            </motion.div>
                )
              ))}
            </AnimatePresence>
            
            {/* Pagination Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {vets.map((_, index) => (
                <button
                  key={index}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 
                           ${activeVet === index ? 'bg-amber-600 w-6' : 'bg-amber-200'}`}
                  onClick={() => setActiveVet(index)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl sm:rounded-2xl 
                         p-6 sm:p-8 lg:p-10 max-w-4xl mx-auto overflow-hidden">
            {/* Background Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-400/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-400/20 rounded-full blur-3xl" />
            
            <div className="relative grid sm:grid-cols-5 gap-6 sm:gap-8 items-center">
              <div className="sm:col-span-3">
                <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm 
                             px-3 py-1.5 rounded-full mb-4">
                  <Heart className="h-4 w-4 text-white" />
                  <span className="text-white text-sm font-medium">Cuidado Premium</span>
                </div>
                
                <h3 className="text-white font-bold text-2xl sm:text-3xl mb-3 sm:mb-4">
                  Atención veterinaria profesional al alcance de un clic
            </h3>
                <p className="text-amber-100 text-base sm:text-lg mb-6 sm:mb-8">
                  Nuestros veterinarios certificados están listos para ayudarte con cualquier problema de salud de tu mascota, desde la comodidad de tu hogar
            </p>
                
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button
              className="bg-white text-amber-600 hover:bg-amber-50 shadow-lg hover:shadow-xl 
                             transition-all duration-300 text-base sm:text-lg py-3 px-6 rounded-xl"
              onClick={() => window.location.href = '/?sign-in=true'}
            >
              Consultar Ahora
                    <VideoIcon className="ml-2 h-5 w-5" />
                  </Button>
                  
                  <Button
                    className="bg-transparent border-2 border-white text-white hover:bg-white/10 
                             transition-all duration-300 text-base sm:text-lg py-3 px-6 rounded-xl"
                    onClick={() => document.getElementById('vets').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Ver Especialistas
                    <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
                </div>
              </div>
              
              <div className="hidden sm:block sm:col-span-2">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <VideoIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-lg">Consulta Virtual</h4>
                      <p className="text-amber-100 text-sm">Conexión inmediata</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-4">
                    {[
                      "Diagnóstico profesional",
                      "Recomendaciones personalizadas",
                      "Seguimiento continuo"
                                        ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-amber-100">
                        <CheckCircle2 className="h-5 w-5 text-amber-100 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="bg-white/10 rounded-lg p-3">
                    <p className="text-white text-sm text-center">
                      Consulta nuestros especialistas
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6 sm:mt-8">
            <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm 
                         border border-amber-100 flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-600" />
              <span className="text-sm text-amber-800">Consultas seguras y privadas</span>
            </div>
            <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm 
                         border border-amber-100 flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-amber-600" />
              <span className="text-sm text-amber-800">Veterinarios verificados</span>
            </div>
            <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm 
                         border border-amber-100 flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              <span className="text-sm text-amber-800">Disponibilidad 24/7</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VetsSection;